import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Copy, RefreshCw, Fingerprint } from 'lucide-react';

const UUIDGeneratorPage = () => {
  const { toast } = useToast();
  const [uuids, setUuids] = useState([]);
  const [count, setCount] = useState(1);

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const generate = () => {
    const newCount = Math.max(1, Math.min(50, parseInt(count) || 1));
    const newUuids = Array.from({ length: newCount }, () => generateUUID());
    setUuids(newUuids);
  };

  const copyUUID = (uuid) => {
    navigator.clipboard.writeText(uuid);
    toast({
      title: 'Copied!',
      description: 'UUID copied to clipboard',
    });
  };

  const copyAll = () => {
    navigator.clipboard.writeText(uuids.join('\n'));
    toast({
      title: 'Copied!',
      description: `${uuids.length} UUIDs copied to clipboard`,
    });
  };

  return (
    <ToolLayout
      title="UUID Generator"
      description="Generate unique identifiers (UUID v4) for your applications. Create single or multiple UUIDs at once."
      icon={Fingerprint}
    >
      <div className="space-y-6">
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-xs">
            <Label htmlFor="count">Number of UUIDs (1-50)</Label>
            <Input
              id="count"
              type="number"
              min="1"
              max="50"
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>
          <Button onClick={generate}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Generate
          </Button>
        </div>

        {uuids.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label>Generated UUIDs ({uuids.length})</Label>
              <Button onClick={copyAll} variant="outline" size="sm">
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {uuids.map((uuid, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                  <code className="flex-1 font-mono text-sm">{uuid}</code>
                  <Button onClick={() => copyUUID(uuid)} variant="ghost" size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

export default UUIDGeneratorPage;